<!--
  @component NewItemModal
  @description Modal for creating a new item with type selection
-->
<script lang="ts">
  import { createEventDispatcher, onDestroy } from 'svelte';
  import { t } from '@/i18n';
  import { newItemModal, hideNewItemModal, setNewItemType } from '@stores/uiStore';
  import { selectedLibraryId, selectedLibraryType } from '@stores/libraryStore';
  import { selectedCollectionKey } from '@stores/collectionStore';
  import { addItem, selectItem } from '@stores/itemStore';
  import { itemActions } from '@services/itemActionsService';
  import { getAvailableItemTypes } from '@/data';
  import { trapFocus, createModalKeyboardHandler } from '@/utils';
  import type { CreateItemInput, ItemType } from '@types';

  const dispatch = createEventDispatcher<{
    created: { key: string };
  }>();

  // Get available item types
  const itemTypes = getAvailableItemTypes();

  // Loading state
  let isCreating = false;
  let modalElement: HTMLDivElement;
  let releaseFocusTrap: (() => void) | null = null;

  // Activate focus trap when modal opens
  $: if ($newItemModal.visible && modalElement) {
    releaseFocusTrap = trapFocus(modalElement, { autoFocus: true });
  }

  // Clean up focus trap on destroy
  onDestroy(() => {
    releaseFocusTrap?.();
  });

  // Handle item type change
  function handleTypeChange(event: Event) {
    const target = event.target as HTMLSelectElement;
    setNewItemType(target.value as ItemType);
  }

  // Handle create
  async function handleCreate() {
    if (!$selectedLibraryId || !$selectedLibraryType || isCreating) return;

    isCreating = true;

    try {
      // Build the input for creating a new item
      const input: CreateItemInput = {
        itemType: $newItemModal.selectedItemType,
        title: '',
        creators: [{ creatorType: 'author', firstName: '', lastName: '' }],
      };

      // Add to collection if one is selected
      if ($selectedCollectionKey && $selectedCollectionKey !== 'all') {
        input.collections = [$selectedCollectionKey];
      }

      const result = await itemActions.createItem($selectedLibraryId, $selectedLibraryType, input);

      if (result.success) {
        // Add to store and select
        addItem(result.data);
        selectItem(result.data);
        dispatch('created', { key: result.data.key });
        hideNewItemModal();
      }
    } finally {
      isCreating = false;
    }
  }

  // Handle cancel
  function handleCancel() {
    releaseFocusTrap?.();
    releaseFocusTrap = null;
    hideNewItemModal();
  }

  // Centralized keyboard handling
  const keyboard = createModalKeyboardHandler({
    onEscape: handleCancel,
    onEnter: handleCreate,
  });
</script>

{#if $newItemModal.visible}
  <div class="modal-overlay" on:keydown={keyboard.handleKeydown} role="presentation">
    <button
      type="button"
      class="modal-backdrop"
      on:click={handleCancel}
      aria-label={$t('common.close')}
      tabindex="-1"
    ></button>
    <div
      bind:this={modalElement}
      class="modal-content"
      role="dialog"
      aria-modal="true"
      aria-labelledby="new-item-modal-title"
    >
      <div class="modal-header">
        <h3 id="new-item-modal-title" class="modal-title">{$t('item.newItem')}</h3>
        <button class="close-button" on:click={handleCancel} aria-label={$t('common.close')}>
          &times;
        </button>
      </div>

      <div class="modal-body">
        <div class="form-group">
          <label for="item-type-select">{$t('item.selectType')}</label>
          <select
            id="item-type-select"
            value={$newItemModal.selectedItemType}
            on:change={handleTypeChange}
          >
            {#each itemTypes as itemType}
              <option value={itemType.value}>
                {itemType.icon}
                {$t(itemType.labelKey)}
              </option>
            {/each}
          </select>
        </div>
      </div>

      <div class="modal-footer">
        <button class="btn btn-secondary" on:click={handleCancel} disabled={isCreating}>
          {$t('common.cancel')}
        </button>
        <button class="btn btn-primary" on:click={handleCreate} disabled={isCreating}>
          {#if isCreating}
            {$t('common.creating')}...
          {:else}
            {$t('common.create')}
          {/if}
        </button>
      </div>
    </div>
  </div>
{/if}

<style>
  .modal-overlay {
    position: fixed;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
  }

  .modal-backdrop {
    position: absolute;
    inset: 0;
    background: rgba(0, 0, 0, 0.6);
    border: none;
    cursor: default;
  }

  .modal-content {
    position: relative;
    background: var(--color-bg-secondary, #383838);
    border: 1px solid var(--color-border, #555);
    border-radius: 8px;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.4);
    min-width: 400px;
    max-width: 500px;
  }

  .modal-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 16px 20px;
    border-bottom: 1px solid var(--color-border, #555);
  }

  .modal-title {
    margin: 0;
    font-size: 18px;
    font-weight: 600;
    color: var(--color-text-primary, #e8e8e8);
  }

  .close-button {
    background: none;
    border: none;
    color: var(--color-text-secondary, #aaa);
    font-size: 24px;
    cursor: pointer;
    padding: 0;
    line-height: 1;
  }

  .close-button:hover {
    color: var(--color-text-primary, #e8e8e8);
  }

  .modal-body {
    padding: 20px;
  }

  .form-group {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .form-group label {
    font-size: 14px;
    font-weight: 500;
    color: var(--color-text-primary, #e8e8e8);
  }

  .form-group select {
    padding: 10px 12px;
    background: var(--color-bg-primary, #2b2b2b);
    border: 1px solid var(--color-border, #555);
    border-radius: 4px;
    color: var(--color-text-primary, #e8e8e8);
    font-size: 14px;
    cursor: pointer;
  }

  .form-group select:focus {
    outline: none;
    border-color: var(--color-accent, #5c7cfa);
  }

  .modal-footer {
    display: flex;
    justify-content: flex-end;
    gap: 12px;
    padding: 16px 20px;
    border-top: 1px solid var(--color-border, #555);
  }

  .btn {
    padding: 10px 20px;
    border: none;
    border-radius: 4px;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    transition: background-color 0.2s;
  }

  .btn:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  .btn-secondary {
    background: var(--color-bg-tertiary, #4a4a4a);
    color: var(--color-text-primary, #e8e8e8);
  }

  .btn-secondary:hover:not(:disabled) {
    background: var(--color-bg-hover, #555);
  }

  .btn-primary {
    background: var(--color-accent, #5c7cfa);
    color: white;
  }

  .btn-primary:hover:not(:disabled) {
    background: var(--color-accent-hover, #4c6ef5);
  }
</style>
